"use client";

import React from "react";
import { motion } from "framer-motion";
import { Globe, Code2, Search, Award, ArrowRight, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const pillars = [{
  title: "Technical Setup",
  description: "Careful hreflang implementation, resilient international URL structures (ccTLDs, subdirectories, or subdomains), and crawlable language switchers that help search engines deliver the right locale without indexing confusion.",
  icon: Code2
}, {
  title: "Content Localisation",
  description: "Granular search behaviour analysis within each target territory. I adapt terminology, search intent, currency, units, and cultural nuances rather than relying on direct, automated translation.",
  icon: Search
}, {
  title: "Market-Specific Authority",
  description: "Securing relevant regional backlinks, digital PR coverage, and local citations within each target jurisdiction to establish genuine local relevance in regional search indexes.",
  icon: Award
}];
const signals = ["Non-UK organic traffic is already arriving on your site, but international visitors leave without buying or enquiring.", "You are preparing to launch products, services, or regional offices into European, North American, or global markets.", "Separate country pages or regional sites are competing with each other in search results, showing the wrong currency or language.", "Overseas advertising and search spend is generating clicks, but conversions remain low due to lack of local resonance."];
export default function WhatIsInternationalSeo() {
  return <section id="whatisinternationalseo" data-nav="light" className="relative overflow-hidden bg-background text-foreground py-20 md:py-28 border-b border-border/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6,
        ease: "easeOut"
      }} className="max-w-3xl mx-auto text-center mb-16">
          <div className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs font-medium tracking-wide uppercase bg-secondary text-secondary-foreground border border-accent/20">
              <Globe className="size-3.5 text-accent mr-1.5" />
              Global Search Architecture
            </Badge>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6">
            What Is International SEO?
          </h2>

          <div className="space-y-4 text-base sm:text-lg text-muted-foreground leading-relaxed font-default text-left sm:text-center">
            <p>
              International SEO is the practice of configuring and optimising your website so search engines understand which version of your content is intended for which country or language, ensuring people in each market find content matching their local search behaviour.
            </p>
            <p>
              Unlike a single-country{" "}
              <Link to="/NationalSeoPage" className="text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors">
                National SEO
              </Link>{" "}
              campaign, international visibility introduces multi-language, multi-region architecture decisions, reciprocal alternate annotations, and distinct local SERP competitors. The outcomes that matter are qualified overseas organic visits, inbound enquiries from priority territories, and measurable commercial return—not vanity rankings for their own sake.
            </p>
          </div>
        </motion.div>

        {/* Three Pillars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {pillars.map((pillar, index) => {
          const Icon = pillar.icon;
          return <motion.div key={pillar.title} data-index={index} initial={{
            opacity: 0,
            y: 24
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.6,
            delay: index * 0.12,
            ease: "easeOut"
          }}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-all duration-300">
                  <CardHeader className="flex flex-row items-center gap-4 pb-2">
                    <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                      <Icon className="size-5" />
                    </div>
                    <CardTitle className="font-serif text-xl font-semibold text-card-foreground">
                      {pillar.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <p className="text-muted-foreground text-sm sm:text-base leading-relaxed font-default">
                      {pillar.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* When a Business Needs It */}
        <div className="bg-card border border-border rounded-2xl p-6 sm:p-10 mb-12 shadow-xs">
          <div className="max-w-3xl mb-6">
            <h3 className="text-xl sm:text-2xl font-semibold text-foreground font-default mb-3">
              When Does a Business Need International SEO?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
              Most companies recognise the need for international SEO when one of the following inflection points occurs:
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {signals.map((signal, idx) => <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-background border border-border/80">
                <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                <p className="text-xs sm:text-sm text-foreground/90 leading-relaxed font-default">
                  {signal}
                </p>
              </div>)}
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        delay: 0.2
      }} className="flex flex-col sm:flex-row items-center justify-center gap-4 text-center">
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-sm px-8 h-12 text-base font-medium rounded-md group">
            <Link to="/ContactPage">
              <span>Book a Free SEO Consultation</span>
              <ArrowRight className="size-4 ml-1 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </Button>
          <Button asChild variant="outline" size="lg" className="px-8 h-12 text-base font-medium rounded-md">
            <Link to="/CaseStudiesPage">
              Explore Case Studies
            </Link>
          </Button>
        </motion.div>
      </div>
    </section>;
}